import { Button, FlexBox, Text } from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import type {
  EntryEventTrigger,
  RoutedThroughAutomationEntry,
} from 'lib-shared-legacy-types';
import {
  AutomationActivityType,
  ConversationAutomationLogEntry,
} from 'lib-shared-types-api';
import { useCallback } from 'react';

import { conversationsApi } from 'apis/conversations';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { LogEntry } from './logEntry';
import { RoutedThroughBusinessHours } from './routedThroughAutomationNodes/components/businessHours';
import { RoutedThroughClose } from './routedThroughAutomationNodes/components/close';
import { RoutedThroughCondition } from './routedThroughAutomationNodes/components/condition';
import { RoutedThroughCustomAttribute } from './routedThroughAutomationNodes/components/customAttributes';
import { RoutedThroughFinish } from './routedThroughAutomationNodes/components/finish';
import { RoutedThroughIntegration } from './routedThroughAutomationNodes/components/integration';
import { RoutedThroughInternalNote } from './routedThroughAutomationNodes/components/internalNote';
import { RoutedThroughQueue } from './routedThroughAutomationNodes/components/queue';
import { RoutedThroughReply } from './routedThroughAutomationNodes/components/reply';
import { RoutedThroughSideConversation } from './routedThroughAutomationNodes/components/sideConversation';
import { RoutedThroughStop } from './routedThroughAutomationNodes/components/stop';
import { RoutedThroughTag } from './routedThroughAutomationNodes/components/tag';
import { RoutedThroughThirdPartyIntegration } from './routedThroughAutomationNodes/components/thirdPartyIntegration';
import { RoutedThroughWait } from './routedThroughAutomationNodes/components/wait';

type Props = {
  entry: RoutedThroughAutomationEntry;
};

export const getRoutedNode = (
  node: ConversationAutomationLogEntry,
): JSX.Element => {
  switch (node.activity_type) {
    case AutomationActivityType.AUTOMATION_CONDITION_NODE_VISITED:
      return <RoutedThroughCondition node={node} />;
    case AutomationActivityType.AUTOMATION_WAIT_NODE_VISITED:
      return <RoutedThroughWait node={node} />;
    case AutomationActivityType.AUTOMATION_QUEUE_NODE_VISITED:
      return <RoutedThroughQueue node={node} />;
    case AutomationActivityType.AUTOMATION_TAG_NODE_VISITED:
      return <RoutedThroughTag node={node} />;
    case AutomationActivityType.AUTOMATION_BUSINESS_HOURS_NODE_VISITED:
      return <RoutedThroughBusinessHours node={node} />;
    case AutomationActivityType.AUTOMATION_REPLY_NODE_VISITED:
      return <RoutedThroughReply />;
    case AutomationActivityType.AUTOMATION_CLOSE_NODE_VISITED:
      return <RoutedThroughClose />;
    case AutomationActivityType.AUTOMATION_EXTERNAL_INTEGRATION_NODE_VISITED:
      return <RoutedThroughIntegration node={node} />;
    case AutomationActivityType.AUTOMATION_STOP_NODE_VISITED:
      return <RoutedThroughStop />;
    case AutomationActivityType.AUTOMATION_CONTACT_CUSTOM_FIELDS_NODE_VISITED:
    case AutomationActivityType.AUTOMATION_CONVERSATION_CUSTOM_FIELDS_NODE_VISITED:
      return <RoutedThroughCustomAttribute node={node} />;
    case AutomationActivityType.AUTOMATION_FINISHED:
      return <RoutedThroughFinish />;
    case AutomationActivityType.AUTOMATION_SIDE_CONVERSATION_NODE_VISITED:
      return <RoutedThroughSideConversation node={node} />;
    case AutomationActivityType.AUTOMATION_THIRD_PARTY_INTEGRATION_NODE_VISITED:
      return <RoutedThroughThirdPartyIntegration node={node} />;
    case AutomationActivityType.AUTOMATION_INTERNAL_NOTE_NODE_VISITED:
      return <RoutedThroughInternalNote node={node} />;
    default:
      return <Text.Paragraph>Unrecognized action</Text.Paragraph>;
  }
};

const getTriggerType = (trigger: EntryEventTrigger): JSX.Element | null => {
  const { type } = trigger;

  if (type === 'message_added') {
    return <>{`Triggered by: Message added by ${trigger.sender}`}</>;
  }

  if (type === 'added_to_queue') {
    return <>Triggered by: Conversation added to queue</>;
  }

  if (type === 'conversation_assigned') {
    return <>Triggered by: Conversation assigned to agent</>;
  }

  if (type === 'conversation_abandoned') {
    return <>Triggered by: Conversation abandoned</>;
  }

  if (type === 'conversation_closed') {
    return <>Triggered by: Conversation closed</>;
  }

  if (type === 'conversation_tagged') {
    return <>Triggered by: Tag added</>;
  }

  if (type === 'conversation_untagged') {
    return <>Triggered by: Tag removed</>;
  }

  if (type === 'custom_fields_updated') {
    return <>Triggered by: Conversation custom attribute updated</>;
  }

  if (type === 'first_message_added') {
    return <>Triggered by: Conversation created</>;
  }

  return null;
};

export const RoutedThroughAutomation = ({
  entry,
}: Props): JSX.Element | null => {
  const { csid, execution_id: executionId, rule_name: automationName } = entry;

  const navigate = useAppNavigate();

  const { automationActivities } =
    conversationsApi.useReadConversationAutomationActivitiesQuery(
      {
        csid,
        automationRunId: executionId ?? '',
      },
      {
        skip: !executionId,
        selectFromResult: ({ data }) => ({
          automationActivities: data?.data.automation_activities ?? [],
        }),
      },
    );

  const hasAutomationReadAccess = useHasAccess('settings.flow:r');

  const handleGoToAutomation = useCallback(() => {
    navigate({
      path: '/settings/automations/flow/:automationId',
      params: {
        path: {
          automationId: entry.rule_id,
        },
      },
    });
  }, [entry.rule_id, navigate]);

  if (!executionId) {
    return null;
  }

  return (
    <LogEntry
      entry={entry}
      icon="route"
      text={`Routed through automation${
        automationName ? `: ${automationName}` : ''
      }`}
      isExpandable
    >
      <FlexBox
        padding="0 0 0 300"
        margin="0 0 0 500"
        gap="200"
        flexDirection="column"
      >
        {entry.event_trigger?.type && (
          <Text.Paragraph variant="2">
            {getTriggerType(entry.event_trigger)}
          </Text.Paragraph>
        )}

        {automationActivities.map((node, index) => (
          <Text.Paragraph key={index} variant="2" subdued>
            {getRoutedNode(node)}
          </Text.Paragraph>
        ))}

        {hasAutomationReadAccess && (
          <FlexBox margin="300 0">
            <Button variant="secondary" onClick={handleGoToAutomation}>
              Go to automation
            </Button>
          </FlexBox>
        )}
      </FlexBox>
    </LogEntry>
  );
};
